import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { GridService } from '@services/grid.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import { ColDef } from 'ag-grid-enterprise';
import { IconSanitizerService } from 'ngx-lib';
import { ColumnSelectorComponent } from './column-selector/column-selector.component';

export interface ICol {
    name: string;
}

export interface IGroupLevel {
    name: string;
    level: number;
}

const GROUPING_LEVELS: IGroupLevel[] = [
    { name: 'Grouping level 1', level: 1 },
    { name: 'Grouping level 2', level: 2 },
    { name: 'Grouping level 3', level: 3 }
];

@Component({
    selector: 'app-group-by-rows-filter',
    templateUrl: './group-by-rows-filter.component.html',
    styleUrls: ['./group-by-rows-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, ...CDK_IMPORTS, ColumnSelectorComponent]
})
export class GroupByRowsFilterComponent {
    cols = input<ColDef[]>([]);

    readonly groupingLevels = GROUPING_LEVELS;
    readonly isOverlayGroupsOpen = signal(false);
    readonly groupOpened = signal<IGroupLevel | null>(null);
    readonly rowGroupingCols = computed(() => this.cols().filter((col: ColDef) => col.enableRowGroup));

    readonly appliedGroups = signal<ColDef[]>([]);
    readonly lastGroupsApplied = signal<ColDef[]>([]);

    private readonly gridService = inject(GridService);
    private readonly iconSanitizer = inject(IconSanitizerService);

    readonly groupByIcon = this.iconSanitizer.getIcon('groupBy');
    readonly arrowIcon = this.iconSanitizer.getIcon('nextArrow');

    groupIsEnable(group: IGroupLevel): boolean {
        return group.level <= this.appliedGroups().length + 1;
    }

    restoreLastGroupsApplied(): void {
        this.appliedGroups.set([...this.lastGroupsApplied()]);
    }

    getAvailableCols(groupOpened: IGroupLevel): ColDef[] {
        const appliedGroups = this.appliedGroups();
        return this.rowGroupingCols().filter(
            (col: ColDef) =>
                !appliedGroups
                    .filter((group) => !!group)
                    .some((group) => this.getColKey(group) === this.getColKey(col)) ||
                appliedGroups[groupOpened.level - 1]?.headerName === col.headerName
        );
    }

    applyGroup(group: IGroupLevel, col: ColDef): void {
        const updated = [...this.appliedGroups()];
        updated[group.level - 1] = col;
        this.appliedGroups.set(updated);
        this.groupOpened.set(null);
        this.applyGroups(true);
    }

    openLevel(group: IGroupLevel, event: Event): void {
        event.stopPropagation();
        this.groupOpened.set(group);
    }

    applyGroups(keepOpen = false): void {
        const appliedGroups = this.appliedGroups();
        const appliedGroupNames = this.gridService.getColsNames(appliedGroups);
        this.gridService.setGroupingState(appliedGroupNames);
        this.gridService.setGroupingStateValue(appliedGroups);
        this.lastGroupsApplied.set([...appliedGroups]);
        this.isOverlayGroupsOpen.set(keepOpen);
    }

    closeOverlay(): void {
        this.groupOpened.set(null);
    }

    closeGroupsOverlay(): void {
        this.isOverlayGroupsOpen.set(false);
    }

    onOverlayOutsideClick(): void {
        this.restoreLastGroupsApplied();
        this.closeGroupsOverlay();
    }

    openCloseGroups(event: Event): void {
        event.stopPropagation();
        if (!this.isOverlayGroupsOpen()) {
            this.syncDraftFromGrid();
        }
        this.isOverlayGroupsOpen.update((open) => !open);
    }

    clearSelection(): void {
        const appliedGroups = this.appliedGroups();
        if (appliedGroups.length) {
            this.gridService.clearSortFromCols(
                appliedGroups.map((col) => this.getColKey(col)).filter((key): key is string => !!key)
            );
        }
        this.appliedGroups.set([]);
        this.lastGroupsApplied.set([]);
        this.applyGroups();
    }

    private syncDraftFromGrid(): void {
        const groups = [...this.gridService.groupingState()];
        this.appliedGroups.set(groups);
        this.lastGroupsApplied.set(groups);
    }

    private getColKey(col: ColDef): string | undefined {
        return col.colId ?? col.field;
    }
}
